/**
 * Leads page filter presets + localStorage persistence.
 * Selection sticks until the user changes it.
 */

import {
  startOfDay,
  endOfDay,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  format,
  parse,
  isValid,
} from "date-fns";

/**
 * Parse a calendar date (yyyy-MM-dd) as local midnight.
 * Avoids `new Date("yyyy-MM-dd")` UTC-midnight shift across timezones.
 * @param {string|null|undefined} value
 * @returns {Date|null}
 */
export function parseLeadsCalendarDate(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parsed = parse(trimmed, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : null;
  }
  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export const LEADS_CRM_STATUS_STORAGE_KEY = "leads.page.crmStatus";
export const LEADS_DATE_FILTER_STORAGE_KEY = "leads.page.dateFilter";

export const LEADS_DATE_PRESETS = Object.freeze({
  ALL_TIME: "all_time",
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  THIS_WEEK: "this_week",
  LAST_14_DAYS: "last_14_days",
  THIS_MONTH: "this_month",
  LAST_30_DAYS: "last_30_days",
  LAST_MONTH: "last_month",
  CUSTOM: "custom",
});

/** Safe default: do not hide open-queue leads until the user picks a range. */
export const DEFAULT_LEADS_DATE_FILTER = Object.freeze({
  preset: LEADS_DATE_PRESETS.ALL_TIME,
  startDate: null,
  endDate: null,
});

export const DEFAULT_LEADS_CRM_STATUS = "new";

export const LEADS_CRM_STATUS_OPTIONS = Object.freeze([
  "new",
  "marketplace",
  "accepted",
  "rejected",
  "converted",
  "all",
]);

export const LEADS_DATE_PRESET_OPTIONS = Object.freeze([
  { value: LEADS_DATE_PRESETS.TODAY, label: "Today" },
  { value: LEADS_DATE_PRESETS.YESTERDAY, label: "Yesterday" },
  { value: LEADS_DATE_PRESETS.THIS_WEEK, label: "This week" },
  { value: LEADS_DATE_PRESETS.LAST_7_DAYS, label: "Last 7 days" },
  { value: LEADS_DATE_PRESETS.LAST_14_DAYS, label: "Last 14 days" },
  { value: LEADS_DATE_PRESETS.THIS_MONTH, label: "This month" },
  { value: LEADS_DATE_PRESETS.LAST_30_DAYS, label: "Last 30 days" },
  { value: LEADS_DATE_PRESETS.LAST_MONTH, label: "Last month" },
  { value: LEADS_DATE_PRESETS.ALL_TIME, label: "All time" },
  { value: LEADS_DATE_PRESETS.CUSTOM, label: "Custom" },
]);

/**
 * @param {Date} [now]
 * @returns {{ startDate: Date|null, endDate: Date|null }}
 */
export function resolveLeadsDateRange(dateFilter, now = new Date()) {
  const filter = normalizeLeadsDateFilter(dateFilter);
  const preset = filter.preset;

  if (preset === LEADS_DATE_PRESETS.ALL_TIME) {
    return { startDate: null, endDate: null };
  }

  if (preset === LEADS_DATE_PRESETS.CUSTOM) {
    if (!filter.startDate && !filter.endDate) {
      return { startDate: null, endDate: null };
    }
    const parsedStart = parseLeadsCalendarDate(filter.startDate);
    const parsedEnd = parseLeadsCalendarDate(filter.endDate);
    return {
      startDate: parsedStart ? startOfDay(parsedStart) : null,
      endDate: parsedEnd ? endOfDay(parsedEnd) : null,
    };
  }

  switch (preset) {
    case LEADS_DATE_PRESETS.TODAY:
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    case LEADS_DATE_PRESETS.YESTERDAY: {
      const yesterday = subDays(now, 1);
      return { startDate: startOfDay(yesterday), endDate: endOfDay(yesterday) };
    }
    case LEADS_DATE_PRESETS.THIS_WEEK:
      return {
        startDate: startOfWeek(now, { weekStartsOn: 1 }),
        endDate: endOfDay(now),
      };
    case LEADS_DATE_PRESETS.LAST_7_DAYS:
      return {
        startDate: startOfDay(subDays(now, 6)),
        endDate: endOfDay(now),
      };
    case LEADS_DATE_PRESETS.LAST_14_DAYS:
      return {
        startDate: startOfDay(subDays(now, 13)),
        endDate: endOfDay(now),
      };
    case LEADS_DATE_PRESETS.THIS_MONTH:
      return {
        startDate: startOfMonth(now),
        endDate: endOfDay(now),
      };
    case LEADS_DATE_PRESETS.LAST_30_DAYS:
      return {
        startDate: startOfDay(subDays(now, 29)),
        endDate: endOfDay(now),
      };
    case LEADS_DATE_PRESETS.LAST_MONTH: {
      const lastMonth = subMonths(now, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    }
    default:
      return { startDate: null, endDate: null };
  }
}

/**
 * API query params for lead list endpoints.
 * @returns {{ dateFrom?: string, dateTo?: string }}
 */
export function toLeadsDateQueryParams(dateFilter, now = new Date()) {
  const { startDate, endDate } = resolveLeadsDateRange(dateFilter, now);
  const params = {};
  if (startDate) params.dateFrom = startDate.toISOString();
  if (endDate) params.dateTo = endDate.toISOString();
  return params;
}

/**
 * Client-side filter for search results that lack server date params.
 */
export function leadMatchesDateFilter(lead, dateFilter, now = new Date()) {
  const { startDate, endDate } = resolveLeadsDateRange(dateFilter, now);
  if (!startDate && !endDate) return true;

  const raw =
    lead?.createdAt ||
    lead?.primaryLead?.createdAt ||
    lead?.created_at ||
    null;
  if (!raw) return false;

  const created = new Date(raw);
  if (Number.isNaN(created.getTime())) return false;
  if (startDate && created < startDate) return false;
  if (endDate && created > endDate) return false;
  return true;
}

export function formatLeadsDateFilterLabel(dateFilter, now = new Date()) {
  const filter = normalizeLeadsDateFilter(dateFilter);
  if (filter.preset === LEADS_DATE_PRESETS.CUSTOM) {
    const { startDate, endDate } = resolveLeadsDateRange(filter, now);
    if (startDate && endDate) {
      return `${format(startDate, "d MMM yyyy")} – ${format(endDate, "d MMM yyyy")}`;
    }
    if (startDate) return `From ${format(startDate, "d MMM yyyy")}`;
    if (endDate) return `Until ${format(endDate, "d MMM yyyy")}`;
    return "Custom";
  }

  const option = LEADS_DATE_PRESET_OPTIONS.find((item) => item.value === filter.preset);
  return option?.label || "All time";
}

export function normalizeLeadsDateFilter(value) {
  if (!value || typeof value !== "object") {
    return { ...DEFAULT_LEADS_DATE_FILTER };
  }

  const presetValues = Object.values(LEADS_DATE_PRESETS);
  const preset = presetValues.includes(value.preset)
    ? value.preset
    : DEFAULT_LEADS_DATE_FILTER.preset;

  const startDate =
    typeof value.startDate === "string" && value.startDate.trim()
      ? value.startDate
      : null;
  const endDate =
    typeof value.endDate === "string" && value.endDate.trim()
      ? value.endDate
      : null;

  if (preset === LEADS_DATE_PRESETS.CUSTOM) {
    return { preset, startDate, endDate };
  }

  return { preset, startDate: null, endDate: null };
}

export function normalizeLeadsCrmStatus(value) {
  if (typeof value !== "string") return DEFAULT_LEADS_CRM_STATUS;
  return LEADS_CRM_STATUS_OPTIONS.includes(value)
    ? value
    : DEFAULT_LEADS_CRM_STATUS;
}

export function loadLeadsCrmStatus(storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem?.(LEADS_CRM_STATUS_STORAGE_KEY);
    return normalizeLeadsCrmStatus(raw);
  } catch {
    return DEFAULT_LEADS_CRM_STATUS;
  }
}

export function saveLeadsCrmStatus(status, storage = globalThis.localStorage) {
  const normalized = normalizeLeadsCrmStatus(status);
  try {
    storage?.setItem?.(LEADS_CRM_STATUS_STORAGE_KEY, normalized);
  } catch {
    // Ignore quota / private-mode failures; in-memory state still works.
  }
  return normalized;
}

export function loadLeadsDateFilter(storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem?.(LEADS_DATE_FILTER_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_LEADS_DATE_FILTER };
    return normalizeLeadsDateFilter(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_LEADS_DATE_FILTER };
  }
}

export function saveLeadsDateFilter(dateFilter, storage = globalThis.localStorage) {
  const normalized = normalizeLeadsDateFilter(dateFilter);
  try {
    storage?.setItem?.(LEADS_DATE_FILTER_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Ignore quota / private-mode failures; in-memory state still works.
  }
  return normalized;
}
